import React, { forwardRef } from 'react';

import { customScrollbar, transitionAnimation, typographyBody1, getHexToRgb } from 'lib/core';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { SIDE_SHEET_WIDTHS } from '../constants';

const StyledSideSheetContainer = styled.div`
  ${({ theme }) => typographyBody1(theme)};
  ${customScrollbar};
  ${transitionAnimation('margin-right')};
  background-color: ${({ theme }) => theme.color.additional.light.value};
  border: ${({ theme }) => `2px solid rgba(${getHexToRgb(theme.color.additional.dark.value)}, 0.2)`};
  box-sizing: border-box;
  height: 100%;
  margin-right: ${({ isCollapsed, isModal, width }) => (isCollapsed && !isModal ? `-${width}` : 'none')};
  overflow-x: hidden;
  width: ${({ width }) => width};
  z-index: ${({ theme }) => theme.zIndex.sideSheet.value};
  ${({ isModal }) =>
    isModal &&
    css`
      ${transitionAnimation('transform')};
      border-bottom-left-radius: ${({ theme }) => theme.size.borderRadius.large.value};
      border-top-left-radius: ${({ theme }) => theme.size.borderRadius.large.value};
      border: ${({ theme }) => theme.border.sidesheet.value};
      border-bottom: ${({ theme }) => theme.border.sidesheet.value};
      border-left: ${({ theme }) => theme.border.sidesheet.value};
      border-top: ${({ theme }) => theme.border.sidesheet.value};
      box-shadow: ${({ theme }) => theme.elevation[4].value};
      position: absolute;
      transform: ${({ isCollapsed }) => (isCollapsed ? 'translateX(105%)' : 'translateX(0%)')};
    `}
`;

const SideSheetContainer = forwardRef(
  (
    {
      children = undefined,
      className = '',
      dataTestId = undefined,
      isCollapsed = true,
      isModal = false,
      width = SIDE_SHEET_WIDTHS.STANDARD,
      ...other
    },
    ref
  ) => (
    <StyledSideSheetContainer
      data-testid={dataTestId ? `${dataTestId}-side-sheet-container` : undefined}
      {...{ className, isCollapsed, isModal, width }}
      {...other}
      ref={ref}
    >
      {!isCollapsed && children}
    </StyledSideSheetContainer>
  )
);

SideSheetContainer.propTypes = {
  /** Allows you to pass additional children components */
  children: PropTypes.node,
  /** Adds additional className to SideSheet to override styling */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** If true, navigation is collapsed */
  isCollapsed: PropTypes.bool,
  /** If true, container is styled with modal variant css properties */
  isModal: PropTypes.bool,
  /** Define Side Sheet Container width if you want to adjust the default variant's width */
  width: PropTypes.string,
};

export { SideSheetContainer };
